import React, { useRef, useState, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import { formatBytes } from '@/utils/utils';
import { useChatStore } from './useChat';
import { useSupabaseStore } from './supabaseStore';
import { SUPABASE_BUCKET } from './types';
import { FileText, Paperclip, Image } from 'react-feather';
import IconButton from '@/UIlibrary/IconButton/IconButton';
import Dropdown from '@/UIlibrary/Dropdown/Dropdown';
import DropdownMenu from '@/UIlibrary/DropdownMenu/DropdownMenu';
import DropdownItem from '@/UIlibrary/DropdownItem/DropdownItem';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { BRAND_COLORS, PURPLE_COLORS } from '@/UIlibrary/globalTheme';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import logger from '@/logger';
import toast from 'react-hot-toast';

const MessageAttachmentDropdown: React.FC<{ chatId: string }> = ({ chatId }) => {
	const t = useTranslations('Support');
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [openConfirmModal, setOpenConfirmModal] = useState(false);
	const [fileType, setFileType] = useState<string | null>(null);
	const [isSending, setIsSending] = useState(false);
	const { sendImageMessage, sendDocumentMessage } = useChatStore();

	const supabase = useSupabaseStore((state) => state.supabase);

	const documentInputRef = useRef<HTMLInputElement>(null);
	const imageInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (selectedFile) {
			setOpenConfirmModal(true);
		}
	}, [selectedFile]);

	const handleDocumentSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (files && files.length > 0) {
			const selectedFile = files[0];
			logger.debug('Selected document:', selectedFile);
			setSelectedFile(selectedFile);
		}
	};

	const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (files && files.length > 0) {
			const selectedFile = files[0];
			logger.debug('Selected image:', selectedFile);
			setSelectedFile(selectedFile);
		}
	};

	const handleConfirm = async (file: File | null) => {
		if (!file) return;
		if (file.size > 5 * 1024 * 1024) {
			toast.error(t('AttachmentSizeLimit'));
			setOpenConfirmModal(false);
			return;
		}
		try {
			setIsSending(true);

			const messageId = `${Date.now()}-${Math.random().toString(36)?.substr(2, 9)}`;

			const filename = `${chatId}/${messageId}-${file.name}`;
			const bucket =
				fileType === 'image' ? SUPABASE_BUCKET.WHATSAPP_IMAGES : SUPABASE_BUCKET.WHATSAPP_DOCUMENTS;

			// Upload to Supabase Storage with organized structure
			const { error: uploadError } = await supabase.storage.from(bucket).upload(filename, file, {
				contentType: file.type,
				cacheControl: '3600',
				upsert: true
			});

			if (uploadError) throw uploadError;

			const {
				data: { publicUrl }
			} = supabase.storage.from(bucket).getPublicUrl(filename);
			if (fileType === 'document') {
				await sendDocumentMessage(chatId, {
					documentUrl: publicUrl
				});
			} else {
				await sendImageMessage(chatId, {
					imageUrl: publicUrl
				});
			}
		} catch (error) {
			console.error(`Error handling ${fileType} message:`, error);
			toast.error(t('ErrorSendingAttachment'));
		} finally {
			setIsSending(false);
			setOpenConfirmModal(false);
		}
	};

	const openDocumentFileSelect = () => {
		documentInputRef.current?.click();
		setFileType('document');
	};

	const openImageFileSelect = () => {
		imageInputRef.current?.click();
		setFileType('image');
	};

	const documentAcceptedMimeType =
		'application/vnd.ms-powerpoint,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/vnd.ms-excel,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation';

	return (
		<>
			<input
				type="file"
				ref={documentInputRef}
				style={{ display: 'none' }}
				accept={documentAcceptedMimeType}
				onChange={handleDocumentSelect}
			/>
			<input
				type="file"
				ref={imageInputRef}
				style={{ display: 'none' }}
				accept="image/jpeg,image/png"
				onChange={handleImageSelect}
			/>
			<Dropdown
				type={<IconButton icon={<Paperclip />} color="secondary" size="large" />}
				label="attachment-dropdown"
				direction="up"
			>
				<DropdownMenu>
					<DropdownItem
						icon={<FileText className="mr-2" color={PURPLE_COLORS.purple600} />}
						onClick={openDocumentFileSelect}
					>
						{t('Document')}
					</DropdownItem>
					<DropdownItem
						icon={<Image className="mr-2" color={BRAND_COLORS.brand600} />}
						onClick={openImageFileSelect}
					>
						{t('Image')}
					</DropdownItem>
				</DropdownMenu>
			</Dropdown>
			<ConfirmModal
				isOpen={openConfirmModal}
				title={t('SendAttachment')}
				onClose={() => setOpenConfirmModal(false)}
				onCancel={() => setOpenConfirmModal(false)}
				confirmActionText={t('Send')}
				onConfirm={() => handleConfirm(selectedFile)}
				confirmBtnLoading={isSending}
				cancelBtnLoading={isSending}
			>
				<div className="flex flex-col gap-4 items-center justify-center">
					<Typography size="lg" weight="bold">
						{selectedFile?.name}
					</Typography>
					{selectedFile?.type.includes('image') ? (
						<img src={URL.createObjectURL(selectedFile)} alt="attachment" className="w-48 h-48" />
					) : (
						<FileText size={48} />
					)}
					<Typography color="gray" size="sm">
						{formatBytes(selectedFile?.size || 0)}
					</Typography>
				</div>
			</ConfirmModal>
		</>
	);
};

export default MessageAttachmentDropdown;
